import { useMemo } from 'react';
import { parseISO, format, getDay } from 'date-fns';
import type { HeatmapCell } from '@/types/analytics';

interface ActivityHeatmapProps {
  data: HeatmapCell[];
}

const CELL = 12;
const GAP = 3;
const LABEL_WIDTH = 22;
const TOP_PADDING = 16;

function intensity(count: number): string {
  if (count === 0) return 'var(--muted)';
  if (count === 1) return 'var(--chart-4)';
  if (count === 2) return 'var(--chart-3)';
  if (count === 3) return 'var(--chart-2)';
  return 'var(--chart-1)';
}

/**
 * GitHub-style 7-row calendar rendered as plain SVG. Each column is a week
 * (Sunday-first). `data` is expected to be a contiguous list of day cells
 * ordered oldest → newest. Gap days on the first week are blank.
 */
export function ActivityHeatmap({ data }: ActivityHeatmapProps) {
  const { weeks, monthLabels } = useMemo(() => {
    if (data.length === 0) return { weeks: [] as HeatmapCell[][], monthLabels: [] as { x: number; label: string }[] };
    const cells = data.map((c) => ({ cell: c, dow: getDay(parseISO(c.date)) }));
    const weeks: (HeatmapCell | null)[][] = [];
    let current: (HeatmapCell | null)[] = Array(7).fill(null);
    const firstDow = cells[0].dow;
    for (let i = 0; i < firstDow; i++) current[i] = null;
    for (const { cell, dow } of cells) {
      current[dow] = cell;
      if (dow === 6) {
        weeks.push(current);
        current = Array(7).fill(null);
      }
    }
    if (current.some((c) => c !== null)) weeks.push(current);

    const monthLabels: { x: number; label: string }[] = [];
    let lastMonth = '';
    weeks.forEach((w, wi) => {
      const first = w.find((c) => c);
      if (!first) return;
      const m = format(parseISO(first.date), 'MMM');
      if (m !== lastMonth) {
        monthLabels.push({ x: LABEL_WIDTH + wi * (CELL + GAP), label: m });
        lastMonth = m;
      }
    });
    return { weeks: weeks as HeatmapCell[][], monthLabels };
  }, [data]);

  if (weeks.length === 0) {
    return <div className="text-sm text-muted-foreground">No activity yet</div>;
  }

  const width = LABEL_WIDTH + weeks.length * (CELL + GAP);
  const height = TOP_PADDING + 7 * (CELL + GAP);
  const dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height} role="img" aria-label="Activity heatmap">
        {monthLabels.map((m, i) => (
          <text
            key={i}
            x={m.x}
            y={TOP_PADDING - 4}
            fontSize={10}
            fill="currentColor"
            className="fill-muted-foreground"
          >
            {m.label}
          </text>
        ))}
        {dayLabels.map((l, i) =>
          l ? (
            <text
              key={i}
              x={0}
              y={TOP_PADDING + i * (CELL + GAP) + CELL - 1}
              fontSize={9}
              fill="currentColor"
              className="fill-muted-foreground"
            >
              {l}
            </text>
          ) : null,
        )}
        {weeks.map((w, wi) =>
          w.map((cell, di) => {
            if (!cell) return null;
            return (
              <rect
                key={`${wi}-${di}`}
                x={LABEL_WIDTH + wi * (CELL + GAP)}
                y={TOP_PADDING + di * (CELL + GAP)}
                width={CELL}
                height={CELL}
                rx={2}
                ry={2}
                fill={intensity(cell.session_count)}
              >
                <title>
                  {format(parseISO(cell.date), 'MMM d, yyyy')} — {cell.session_count} session
                  {cell.session_count === 1 ? '' : 's'} ({cell.completed_count} done)
                </title>
              </rect>
            );
          }),
        )}
      </svg>
    </div>
  );
}
